import { Link, NavLink, useLocation, useNavigate } from 'react-router';
import { useAuth } from '@/contexts/auth-context';
import { useState, useRef, useEffect } from 'react';
import { clsx } from 'clsx';
import { isAdminEmail } from '@/lib/admin';

export function Header() {
  const { isAuthenticated, user, logout } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [councilDropdownOpen, setCouncilDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const navigate = useNavigate();
  
  const isCouncilPage = location.pathname.includes('authority-ratings') || location.pathname.includes('road-maintenance-report') || location.pathname.includes('councils') || location.pathname.includes('/data/council-road-maintenance-ratings');

  const handleSignOut = async () => {
    setMobileMenuOpen(false);

    try {
      await logout();
    } catch {
      // logout() clears local auth state in its finally block; still return home.
    } finally {
      navigate('/', { replace: true });
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setCouncilDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <header className="bg-white border-b border-surface-200 sticky top-0 z-50">
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center flex-shrink-0">
            <img 
              src="/logo-horizontal.svg" 
              alt="Fixtyer - UK Pothole Damage Claims" 
              className="h-10 w-auto"
            />
          </Link>

          {/* Desktop Navigation - Centered */}
          <div className="hidden md:flex items-center justify-center flex-1 gap-8">
            <NavLink
              to="/how-it-works"
              className={({ isActive }) =>
                clsx(
                  'text-sm font-medium transition-colors',
                  isActive ? 'text-brand-600' : 'text-surface-800 hover:text-brand-600'
                )
              }
            >
              How It Works
            </NavLink>
            <NavLink
              to="/pricing"
              className={({ isActive }) =>
                clsx(
                  'text-sm font-medium transition-colors',
                  isActive ? 'text-brand-600' : 'text-surface-800 hover:text-brand-600'
                )
              }
            >
              Pricing
            </NavLink>
            <NavLink
              to="/guides"
              className={({ isActive }) =>
                clsx(
                  'text-sm font-medium transition-colors',
                  isActive ? 'text-brand-600' : 'text-surface-800 hover:text-brand-600'
                )
              }
            >
              Guides
            </NavLink>
            <NavLink
              to="/blog"
              className={({ isActive }) =>
                clsx(
                  'text-sm font-medium transition-colors',
                  isActive ? 'text-brand-600' : 'text-surface-800 hover:text-brand-600'
                )
              }
            >
              Blog
            </NavLink>
            <NavLink
              to="/about"
              className={({ isActive }) =>
                clsx(
                  'text-sm font-medium transition-colors',
                  isActive ? 'text-brand-600' : 'text-surface-800 hover:text-brand-600'
                )
              }
            >
              About
            </NavLink>
            {/* Council Ratings Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setCouncilDropdownOpen(!councilDropdownOpen)}
                aria-expanded={councilDropdownOpen}
                aria-haspopup="true"
                className={clsx(
                  'text-sm font-medium transition-colors flex items-center gap-1',
                  isCouncilPage ? 'text-brand-600' : 'text-surface-800 hover:text-brand-600'
                )}
              >
                Council Ratings
                <svg 
                  className={clsx('w-4 h-4 transition-transform', councilDropdownOpen && 'rotate-180')} 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              
              {councilDropdownOpen && (
                <div className="absolute top-full left-0 mt-2 w-72 bg-white rounded-xl shadow-lg border border-surface-200 py-2 z-50">
                  <NavLink
                    to="/councils"
                    onClick={() => setCouncilDropdownOpen(false)}
                    aria-describedby="council-reports-desc"
                    className={({ isActive }) =>
                      clsx(
                        'block px-4 py-3 hover:bg-surface-50 transition-colors',
                        isActive && 'bg-brand-50'
                      )
                    }
                  >
                    <div className="text-[14px] font-bold text-brand-900">Council Reports</div>
                    <div id="council-reports-desc" className="text-[12px] text-[#6B7280] mt-0.5">
                      DfT road maintenance reports for every council.
                    </div>
                  </NavLink>
                  <NavLink
                    to="/authority-ratings"
                    onClick={() => setCouncilDropdownOpen(false)}
                    aria-describedby="search-council-desc"
                    className={({ isActive }) =>
                      clsx(
                        'block px-4 py-3 hover:bg-surface-50 transition-colors',
                        isActive && 'bg-brand-50'
                      )
                    }
                  >
                    <div className="text-[14px] font-bold text-brand-900">Search Your Council</div>
                    <div id="search-council-desc" className="text-[12px] text-[#6B7280] mt-0.5">
                      Find inspection rules for your area.
                    </div>
                  </NavLink>
                  <NavLink
                    to="/road-maintenance-report"
                    onClick={() => setCouncilDropdownOpen(false)}
                    aria-describedby="league-table-desc"
                    className={({ isActive }) =>
                      clsx(
                        'block px-4 py-3 hover:bg-surface-50 transition-colors',
                        isActive && 'bg-brand-50'
                      )
                    }
                  >
                    <div className="text-[14px] font-bold text-brand-900">National League Table</div>
                    <div id="league-table-desc" className="text-[12px] text-[#6B7280] mt-0.5">
                      See the 2025-2026 DfT ratings for England.
                    </div>
                  </NavLink>
                  <NavLink
                    to="/data/council-road-maintenance-ratings"
                    onClick={() => setCouncilDropdownOpen(false)}
                    className={({ isActive }) =>
                      clsx(
                        'block px-4 py-3 hover:bg-surface-50 transition-colors',
                        isActive && 'bg-brand-50'
                      )
                    }
                  >
                    <div className="text-[14px] font-bold text-brand-900">Download Dataset</div>
                    <div className="text-[12px] text-[#6B7280] mt-0.5">
                      Citable 2025-2026 v1 CSV and JSON.
                    </div>
                  </NavLink>
                </div>
              )}
            </div>
          </div>

          {/* Desktop Auth/User - Right */}
          <div className="hidden md:flex items-center gap-4 flex-shrink-0">
            {isAuthenticated ? (
              <>
                <NavLink
                  to="/dashboard"
                  className={({ isActive }) =>
                    clsx(
                      'text-sm font-medium transition-colors',
                      isActive ? 'text-brand-600' : 'text-surface-800 hover:text-brand-600'
                    )
                  }
                >
                  Dashboard
                </NavLink>
                <NavLink
                  to="/claims"
                  className={({ isActive }) =>
                    clsx(
                      'text-sm font-medium transition-colors',
                      isActive ? 'text-brand-600' : 'text-surface-800 hover:text-brand-600'
                    )
                  }
                >
                  My Claims
                </NavLink>
                {isAdminEmail(user?.email) && (
                  <NavLink
                    to="/admin"
                    className={({ isActive }) =>
                      clsx(
                        'text-sm font-medium transition-colors',
                        isActive ? 'text-brand-600' : 'text-surface-800 hover:text-brand-600'
                      )
                    }
                  >
                    Admin
                  </NavLink>
                )}
                <div className="flex items-center gap-4 pl-4 border-l border-surface-200">
                  <span className="text-sm text-surface-800">{user?.name || user?.email}</span>
                  <button
                    onClick={() => void handleSignOut()}
                    className="text-sm font-medium text-surface-800 hover:text-error-600 transition-colors"
                  >
                    Sign out
                  </button>
                </div>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  className="text-sm font-medium text-surface-800 hover:text-brand-600 transition-colors"
                >
                  Sign in
                </Link>
                <Link
                  to="/check"
                  className="header-cta-shimmer inline-flex items-center justify-center text-sm font-medium px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition-colors"
                >
                  <span className="header-cta-shimmer__shine" aria-hidden="true" />
                  <span className="relative z-10">Check Your Eligibility</span>
                </Link>
              </>
            )}
          </div>

          {/* Mobile menu button */}
          <button
            type="button"
            className="md:hidden p-2 text-surface-800"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileMenuOpen}
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              {mobileMenuOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-surface-200">
            <div className="flex flex-col gap-4">
              <NavLink
                to="/how-it-works"
                onClick={() => setMobileMenuOpen(false)}
                className={({ isActive }) =>
                  clsx(
                    'text-sm font-medium px-2 py-1',
                    isActive ? 'text-brand-600' : 'text-surface-800'
                  )
                }
              >
                How It Works
              </NavLink>
              <NavLink
                to="/pricing"
                onClick={() => setMobileMenuOpen(false)}
                className={({ isActive }) =>
                  clsx(
                    'text-sm font-medium px-2 py-1',
                    isActive ? 'text-brand-600' : 'text-surface-800'
                  )
                }
              >
                Pricing
              </NavLink>
              <NavLink
                to="/guides"
                onClick={() => setMobileMenuOpen(false)}
                className={({ isActive }) =>
                  clsx(
                    'text-sm font-medium px-2 py-1',
                    isActive ? 'text-brand-600' : 'text-surface-800'
                  )
                }
              >
                Guides
              </NavLink>
              <NavLink
                to="/blog"
                onClick={() => setMobileMenuOpen(false)}
                className={({ isActive }) =>
                  clsx(
                    'text-sm font-medium px-2 py-1',
                    isActive ? 'text-brand-600' : 'text-surface-800'
                  )
                }
              >
                Blog
              </NavLink>
              <NavLink
                to="/about"
                onClick={() => setMobileMenuOpen(false)}
                className={({ isActive }) =>
                  clsx(
                    'text-sm font-medium px-2 py-1',
                    isActive ? 'text-brand-600' : 'text-surface-800'
                  )
                }
              >
                About
              </NavLink>
              {/* Council Ratings Section */}
              <div className="space-y-1">
                <span className="text-xs font-semibold text-surface-400 uppercase tracking-wide px-2">Council Ratings</span>
                <NavLink
                  to="/councils"
                  onClick={() => setMobileMenuOpen(false)}
                  className={({ isActive }) =>
                    clsx(
                      'text-[14px] font-bold px-2 py-1 block',
                      isActive ? 'text-brand-600' : 'text-surface-800'
                    )
                  }
                >
                  Council Reports
                </NavLink>
                <NavLink
                  to="/authority-ratings"
                  onClick={() => setMobileMenuOpen(false)}
                  className={({ isActive }) =>
                    clsx(
                      'text-[14px] font-bold px-2 py-1 block',
                      isActive ? 'text-brand-600' : 'text-surface-800'
                    )
                  }
                >
                  Search Your Council
                </NavLink>
                <NavLink
                  to="/road-maintenance-report"
                  onClick={() => setMobileMenuOpen(false)}
                  className={({ isActive }) =>
                    clsx(
                      'text-[14px] font-bold px-2 py-1 block',
                      isActive ? 'text-brand-600' : 'text-surface-800'
                    )
                  }
                >
                  National League Table
                </NavLink>
                <NavLink
                  to="/data/council-road-maintenance-ratings"
                  onClick={() => setMobileMenuOpen(false)}
                  className={({ isActive }) =>
                    clsx(
                      'text-[14px] font-bold px-2 py-1 block',
                      isActive ? 'text-brand-600' : 'text-surface-800'
                    )
                  }
                >
                  Download Dataset
                </NavLink>
              </div>
              {isAuthenticated ? (
                <>
                  <div className="border-t border-surface-200 pt-4 mt-2">
                    <NavLink
                      to="/dashboard"
                      onClick={() => setMobileMenuOpen(false)}
                      className={({ isActive }) =>
                        clsx(
                          'text-sm font-medium px-2 py-1 block',
                          isActive ? 'text-brand-600' : 'text-surface-800'
                        )
                      }
                    >
                      Dashboard
                    </NavLink>
                  </div>
                  <NavLink
                    to="/claims"
                    onClick={() => setMobileMenuOpen(false)}
                    className={({ isActive }) =>
                      clsx(
                        'text-sm font-medium px-2 py-1',
                        isActive ? 'text-brand-600' : 'text-surface-800'
                      )
                    }
                  >
                    My Claims
                  </NavLink>
                  {isAdminEmail(user?.email) && (
                    <NavLink
                      to="/admin"
                      onClick={() => setMobileMenuOpen(false)}
                      className={({ isActive }) =>
                        clsx(
                          'text-sm font-medium px-2 py-1',
                          isActive ? 'text-brand-600' : 'text-surface-800'
                        )
                      }
                    >
                      Admin
                    </NavLink>
                  )}
                  <button
                    onClick={() => void handleSignOut()}
                    className="text-sm font-medium text-left px-2 py-1 text-error-600"
                  >
                    Sign out
                  </button>
                </>
              ) : (
                <div className="border-t border-surface-200 pt-4 mt-2 flex flex-col gap-3">
                  <Link
                    to="/login"
                    onClick={() => setMobileMenuOpen(false)}
                    className="text-sm font-medium px-2 py-1 text-surface-800"
                  >
                    Sign in
                  </Link>
                  <Link
                    to="/check"
                    onClick={() => setMobileMenuOpen(false)}
                    className="header-cta-shimmer inline-flex items-center justify-center text-sm font-medium px-4 py-2 bg-brand-600 text-white rounded-lg text-center"
                  >
                    <span className="header-cta-shimmer__shine" aria-hidden="true" />
                    <span className="relative z-10">Check Your Eligibility</span>
                  </Link>
                </div>
              )}
            </div>
          </div>
        )}
      </nav>
    </header>
  );
}
